/**
 * useScrollOrchestrator - Precise scroll state management for chat interfaces
 * 
 * Provides a complete solution for:
 * - Anchor-to-bottom behavior during message streaming
 * - Visual position preservation during history prepend
 * - Smooth vs instant scroll based on context
 * - Throttled updates to prevent layout thrashing
 * 
 * Core principle: Only auto-scroll when user is at bottom, 
 * never steal scroll from users who've scrolled up
 */
import { useEffect, useRef, useCallback } from 'react';

/**
 * Throttle utility - ensures function runs at most once per interval
 * @param {Function} fn - Function to throttle
 * @param {number} ms - Minimum milliseconds between calls
 */
export function useThrottled(fn, ms = 120) {
  const timeoutRef = useRef(null);
  const lastRunRef = useRef(0);
  
  return useCallback((...args) => {
    const now = Date.now();
    
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Calculate time since last run
    const timeSinceLastRun = now - lastRunRef.current;
    
    if (timeSinceLastRun >= ms) {
      // Enough time has passed, run immediately
      lastRunRef.current = now;
      fn(...args);
    } else {
      // Schedule for later
      const delay = ms - timeSinceLastRun;
      timeoutRef.current = setTimeout(() => {
        lastRunRef.current = Date.now();
        timeoutRef.current = null;
        fn(...args);
      }, delay);
    }
  }, [fn, ms]);
}

/**
 * Main scroll orchestration hook
 * @param {React.RefObject} scrollerRef - Reference to scrollable container
 * @param {Object} options - Configuration options
 * @returns {Object} Scroll control methods and state
 */
export function useScrollOrchestrator(scrollerRef, options = {}) {
  const {
    bottomThreshold = 2, // Pixels from bottom to consider "at bottom"
    smoothScrollDuration = 300, // Duration for smooth scrolls
    enableDebugLogging = false
  } = options;
  
  // State refs (not causing re-renders)
  const isAtBottomRef = useRef(true);
  const lastMeasurementsRef = useRef({
    scrollHeight: 0,
    clientHeight: 0,
    scrollTop: 0
  });
  const isAutoScrollingRef = useRef(false);
  const scrollListenerPausedRef = useRef(false);
  
  // Debug logger
  const log = useCallback((...args) => {
    if (enableDebugLogging) {
      console.log('[ScrollOrchestrator]', ...args);
    }
  }, [enableDebugLogging]);
  
  /**
   * Measure current scroll metrics
   */
  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return null;
    
    return {
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
      scrollTop: el.scrollTop
    };
  }, [scrollerRef]);
  
  /**
   * Check if scroll is near bottom (within threshold)
   */
  const isNearBottom = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return false;
    
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom <= bottomThreshold;
  }, [scrollerRef, bottomThreshold]);
  
  /**
   * Instant jump to bottom (no animation)
   */
  const jumpToBottom = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    
    isAutoScrollingRef.current = true;
    scrollListenerPausedRef.current = true;
    
    el.scrollTop = el.scrollHeight;
    log('Jumped to bottom');
    
    // Re-enable listener after paint
    requestAnimationFrame(() => {
      isAutoScrollingRef.current = false;
      scrollListenerPausedRef.current = false;
      isAtBottomRef.current = true;
    });
  }, [scrollerRef, log]);
  
  /**
   * Smooth scroll to bottom with easing
   */
  const smoothToBottom = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    
    isAutoScrollingRef.current = true;
    scrollListenerPausedRef.current = true;
    
    el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth'
    });
    log('Smooth scroll to bottom');
    
    // Re-enable listener after animation completes
    setTimeout(() => {
      isAutoScrollingRef.current = false;
      scrollListenerPausedRef.current = false;
      isAtBottomRef.current = true;
    }, smoothScrollDuration);
  }, [scrollerRef, smoothScrollDuration, log]);
  
  /**
   * Track manual user scroll
   */
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    
    const handleScroll = () => {
      // Skip if we're auto-scrolling
      if (scrollListenerPausedRef.current || isAutoScrollingRef.current) {
        return;
      }
      
      const wasAtBottom = isAtBottomRef.current;
      const nowAtBottom = isNearBottom();
      
      if (wasAtBottom !== nowAtBottom) {
        log(`User scroll: ${nowAtBottom ? 'at bottom' : 'scrolled up'}`);
      }
      
      isAtBottomRef.current = nowAtBottom;
    };
    
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isNearBottom, scrollerRef, log]);
  
  /**
   * Snapshot measurements before DOM mutations
   * Call this right before prepending history or other major changes
   */
  const snapshotBeforeMutate = useCallback(() => {
    const measurements = measure();
    if (measurements) {
      lastMeasurementsRef.current = measurements;
      log('Snapshot taken', measurements);
    }
  }, [measure, log]);
  
  /**
   * Adjust scroll after content prepended above viewport
   * Preserves visual position when history loads
   */
  const adjustAfterPrepend = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    
    const before = lastMeasurementsRef.current;
    const now = measure();
    
    if (!now) return;
    
    const heightDelta = now.scrollHeight - before.scrollHeight;
    
    if (heightDelta > 0) {
      // Content was added above, adjust scroll to maintain position
      scrollListenerPausedRef.current = true;
      el.scrollTop = before.scrollTop + heightDelta;
      log(`Adjusted scroll by ${heightDelta}px after prepend`);
      
      requestAnimationFrame(() => {
        scrollListenerPausedRef.current = false;
      });
    }
  }, [scrollerRef, measure, log]);
  
  /**
   * Smart scroll when new content appends at bottom
   * Only scrolls if user was already at bottom
   */
  const maybeStickToBottomOnAppend = useCallback((options = {}) => {
    const { preferSmooth = true, force = false } = options;
    
    if (!scrollerRef.current) return;
    
    // Force scroll or check if at bottom
    if (force || isAtBottomRef.current) {
      if (preferSmooth) {
        smoothToBottom();
      } else {
        jumpToBottom();
      }
    } else {
      log('Not scrolling - user has scrolled up');
    }
  }, [jumpToBottom, smoothToBottom, scrollerRef, log]);
  
  /**
   * Temporarily disable smooth scrolling
   * Useful during keyboard transitions to avoid double-easing
   */
  const temporarilyDisableSmoothScrolling = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    
    const previousBehavior = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.scrollBehavior = previousBehavior;
      });
    });
  }, [scrollerRef]);
  
  /**
   * Get current state (useful for debugging)
   */
  const getState = useCallback(() => {
    return {
      isAtBottom: isAtBottomRef.current,
      isAutoScrolling: isAutoScrollingRef.current,
      measurements: measure()
    };
  }, [measure]);
  
  /**
   * Throttled version of maybeStickToBottomOnAppend
   * Use this for high-frequency updates like streaming
   */
  const throttledStickToBottom = useThrottled(
    maybeStickToBottomOnAppend,
    120 // Adjust every 120ms max during streaming
  );
  
  return {
    // Core actions
    snapshotBeforeMutate,
    adjustAfterPrepend,
    maybeStickToBottomOnAppend,
    throttledStickToBottom,
    jumpToBottom,
    smoothToBottom,
    
    // Utilities
    temporarilyDisableSmoothScrolling,
    isNearBottom,
    getState,
    
    // Refs for direct access if needed
    isAtBottomRef
  };
}

/**
 * Helper hook for intersection-based infinite scroll
 * @param {Function} onIntersect - Callback when sentinel is visible
 * @param {React.RefObject} scrollerRef - Reference to scroll container
 */
export function useInfiniteScrollSentinel(onIntersect, scrollerRef) {
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  
  const sentinelCallback = useCallback((node) => {
    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    if (node && scrollerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            onIntersect();
          }
        },
        {
          root: scrollerRef.current,
          threshold: 0.1
        }
      );
      
      observerRef.current.observe(node);
    }
    
    sentinelRef.current = node;
  }, [onIntersect, scrollerRef]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  
  return sentinelCallback;
}